import { Injectable, OnDestroy, computed, effect, signal, untracked } from '@angular/core';
import { IWorkoutPlanJob } from '@foxera/shared/interfaces/client.interface';
import { SignalrWorkoutJobService } from '@foxera/shared/services/signalr-workout-job.service';
import { WorkoutInfoService } from '@foxera/feature/clients/services/workout-info.service';
import { PlanDialogService } from '@foxera/shared/services/plan-dialog.service';
import { CustomToastService } from '@foxera/shared/services/custom-toast.service';

@Injectable({
  providedIn: 'root',
})
export class WorkoutJobsStateService implements OnDestroy {
  private readonly _jobs = signal<IWorkoutPlanJob[]>([]);
  private readonly _isLoading = signal<boolean>(false);
  private _initialized = false;
  private _durationInterval: number | null = null;

  readonly jobs = computed(() => this._jobs());
  readonly isLoading = computed(() => this._isLoading());

  /** Count of jobs that are Pending or Processing (drives the navbar badge) */
  readonly activeJobsCount = computed(
    () =>
      this._jobs().filter(
        (j) => j.status === 'Pending' || j.status === 'Processing'
      ).length
  );

  /**
   * Computed signal: Map of jobId to formatted duration for job cards (short format: "5s" or "2:30")
   */
  readonly jobDurationsFormatted = computed(() => {
    const durationsMap = new Map<string, string>();
    this._jobs().forEach(job => {
      const totalSeconds = Math.floor(job.durationInSeconds);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      if (minutes === 0) {
        durationsMap.set(job.jobId, `${seconds}s`);
      } else {
        durationsMap.set(job.jobId, `${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    });
    return durationsMap;
  });

  /**
   * Computed signal: Map of jobId to full formatted duration for loading screen (always mm:ss format)
   */
  readonly jobDurationsFullFormatted = computed(() => {
    const durationsMap = new Map<string, string>();
    this._jobs().forEach(job => {
      const totalSeconds = Math.floor(job.durationInSeconds);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      durationsMap.set(job.jobId, `${minutes}:${seconds.toString().padStart(2, '0')}`);
    });
    return durationsMap;
  });

  /**
   * Get formatted duration for a specific job (short format for cards)
   */
  getFormattedDuration(jobId: string): string {
    return this.jobDurationsFormatted().get(jobId) || '0s';
  }

  /**
   * Get full formatted duration for a specific job (mm:ss format for loading screen)
   */
  getFullFormattedDuration(jobId: string): string {
    return this.jobDurationsFullFormatted().get(jobId) || '0:00';
  }

  constructor(
    private signalrService: SignalrWorkoutJobService,
    private workoutInfoService: WorkoutInfoService,
    private planDialogService: PlanDialogService,
    private customToastService: CustomToastService
  ) {
    // React to every SignalR job update and upsert into the local list
    effect(() => {
      const updatedJob = this.signalrService.jobUpdated();
      if (!updatedJob) return;

      // Use untracked to prevent dependency tracking issues
      untracked(() => {
        const currentJobs = this._jobs();
        const existingJob = currentJobs.find((j) => j.jobId === updatedJob.jobId);
        
        // Check if job just completed (was not completed before, is completed now)
        const justCompleted = existingJob && 
                              existingJob.status !== 'Completed' && 
                              updatedJob.status === 'Completed';
        
        this._jobs.update((current) => {
          const index = current.findIndex((j) => j.jobId === updatedJob.jobId);
          if (index >= 0) {
            const next = [...current];
            next[index] = updatedJob;
            return next;
          }
          // New job we haven't seen yet – prepend it
          return [updatedJob, ...current];
        });
        
        // Show success notification when job completes
        if (justCompleted) {
          const clientName = updatedJob.client 
            ? `${updatedJob.client.firstName} ${updatedJob.client.lastName}` 
            : 'Client';
          // Only show the clickable toast if the plan dialog is not already open
          // or if it's open but showing a different job
          // AND if the live workout session is not visible
          const isDialogOpen = this.planDialogService.isVisible();
          const isShowingThisJob = this.planDialogService.activeJobId() === updatedJob.jobId;
          const isLiveSessionVisible = this.planDialogService.isLiveWorkoutSessionVisible();
          
          if (!isLiveSessionVisible && (!isDialogOpen || (isDialogOpen && !isShowingThisJob))) {
            this.customToastService.showSuccessWithLink(
              `${clientName}'s workout plan is ready`,
              () => this.navigateToJob(updatedJob)
            );
          }
        }
      });
    });

    // Start interval to increment duration for active jobs every second
    this.startDurationInterval();
  }

  /**
   * Start an interval that increments duration for all active jobs every second
   */
  private startDurationInterval(): void {
    this._durationInterval = window.setInterval(() => {
      this._jobs.update((current) =>
        current.map((job) => {
          // Only increment for active jobs (Pending or Processing)
          if (job.status === 'Pending' || job.status === 'Processing') {
            return { ...job, durationInSeconds: job.durationInSeconds + 1 };
          }
          return job;
        })
      );
    }, 1000);
  }

  /**
   * Stop the duration interval
   */
  private stopDurationInterval(): void {
    if (this._durationInterval !== null) {
      window.clearInterval(this._durationInterval);
      this._durationInterval = null;
    }
  }

  /**
   * Called once on app startup (from AppShellComponent).
   * Fetches the initial job list and starts the SignalR subscription.
   */
  async initialize(employeeId: number, tenantId: string): Promise<void> {
    if (!employeeId || !tenantId) return;
    if (this._initialized) return;
    this._initialized = true;

    this._isLoading.set(true);
    this.workoutInfoService.getEmployeeJobs(employeeId).subscribe({
      next: (jobs) => {
        this._jobs.set(jobs);
        this._isLoading.set(false);
      },
      error: () => {
        this._isLoading.set(false);
      },
    });

    try {
      await this.signalrService.initialize();
      await this.signalrService.subscribeToEmployee(employeeId, tenantId);
    } catch (error) {
      // SignalR failure is non-blocking – the HTTP poll is the fallback
    }
  }

  /**
   * Cancel a job by ID.
   * Optimistically sets status to Cancelled and calls the API.
   */
  cancelJob(jobId: string): void {
    this._jobs.update((current) =>
      current.map((j) =>
        j.jobId === jobId ? { ...j, status: 'Cancelled' } : j
      )
    );

    this.workoutInfoService.cancelJob(jobId).subscribe({
      error: () => {
        // Revert the optimistic update on failure
        this._jobs.update((current) =>
          current.map((j) =>
            j.jobId === jobId ? { ...j, status: 'Pending' } : j
          )
        );
      },
    });
  }

  /**
   * Retry a failed job by calling the restart endpoint.
   * Optimistically sets status to Pending and calls the API.
   */
  retryJob(jobId: string): void {
    // Optimistically set to Pending
    this._jobs.update((current) =>
      current.map((j) =>
        j.jobId === jobId ? { ...j, status: 'Pending', errorMessage: null } : j
      )
    );

    this.workoutInfoService.restartJob(jobId).subscribe({
      error: () => {
        // Revert the optimistic update on failure
        this._jobs.update((current) =>
          current.map((j) =>
            j.jobId === jobId ? { ...j, status: 'Failed' } : j
          )
        );
      },
    });
  }

  /**
   * Open the plan dialog for a job: loading screen for active jobs, plan viewer for completed.
   * Opens the global dialog without navigation, keeping the user on their current page.
   */
  navigateToJob(job: IWorkoutPlanJob): void {
    if (job.status === 'Pending' || job.status === 'Processing') {
      this.planDialogService.openPlanDialogWithJob(job.clientId, job.jobId);
    } else if (job.status === 'Completed' && job.workoutPlanId != null) {
      this.planDialogService.openPlanDialog(job.clientId, job.workoutPlanId);
    }
  }

  ngOnDestroy(): void {
    this.stopDurationInterval();
    this.signalrService.disconnect();
  }
}
